

import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Grid, Paper, Typography, TextField, Button,
  InputAdornment, Chip, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Avatar, MenuItem,
  Select, FormControl, Dialog, DialogContent,
  IconButton, Tooltip, useMediaQuery, useTheme,
  CircularProgress, Snackbar, Alert,
  Popper, ClickAwayListener, Collapse,
} from '@mui/material';
import {
  Search, PhoneEnabled, Done, Close, ArrowForward,
  Facebook, WhatsApp, Groups, DirectionsWalk, History,
  FiberManualRecord, PersonOutlined, Refresh, InfoOutlined,
  Visibility, CalendarToday,
  LocationOn, CellTower, ExpandMore, Clear,
  KeyboardArrowUp, KeyboardArrowDown, Remove,
  Phone, Mail,
  CheckCircle,
  Delete as DeleteIcon,
  FilterList,
} from '@mui/icons-material';
import { URL } from "../config";
import toast, { Toaster } from 'react-hot-toast';

// ─── Font ─────────────────────────────────────────────────────────────────────
const fontLink = document.createElement('link');
fontLink.rel = 'stylesheet';
fontLink.href = 'https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700;9..40,800&display=swap';
if (!document.head.querySelector('[href*="DM+Sans"]')) document.head.appendChild(fontLink);

// ─── Constants ────────────────────────────────────────────────────────────────
const FONT = "'DM Sans', sans-serif";
const NAVY = '#0f1f5c';
const ORANGE = '#f97316';
const LABEL = '#03070e';

const PRIORITY_OPTIONS = [
  { value: 1, label: 'High', color: '#991b1b', bg: '#fee2e2', border: '#fecaca', icon: <KeyboardArrowUp /> },
  { value: 2, label: 'Medium', color: '#92400e', bg: '#fef3c7', border: '#fde68a', icon: <Remove /> },
  { value: 3, label: 'Low', color: '#166534', bg: '#dcfce7', border: '#bbf7d0', icon: <KeyboardArrowDown /> },
];

const normP = (v) => (v === null || v === undefined || v === 0 || v === '0') ? 2 : Number(v);

const getPriorityOption = (val) => {
  if (val === 1 || val === '1' || val === 'high') return PRIORITY_OPTIONS[0];
  if (val === 2 || val === '2' || val === 'medium') return PRIORITY_OPTIONS[1];
  if (val === 3 || val === '3' || val === 'low') return PRIORITY_OPTIONS[2];
  return PRIORITY_OPTIONS[1];
};

const MEDIA_ICON_MAP = {
  call: <PhoneEnabled />,
  wp: <WhatsApp />,
  whatsapp: <WhatsApp />,
  facebook: <Facebook />,
  visit: <DirectionsWalk />,
  meeting: <Groups />,
  instagram: <PhoneEnabled />,
  phonecall: <PhoneEnabled />,
  sms: <Phone />,
  email: <Mail />,
};

const MEDIA_CIRCLE_COLORS = {
  whatsapp: '#25d366',
  wp: '#25d366',
  sms: '#6b7280',
  call: '#3b82f6',
  phonecall: '#3b82f6',
  facebook: '#1877f2',
  instagram: '#e1306c',
  visit: '#f97316',
  meeting: '#8b5cf6',
  email: '#6366f1',
};

const STATUS_PALETTE = [
  { color: '#1e40af', bg: '#dbeafe', border: '#bfdbfe' },
  { color: '#166534', bg: '#dcfce7', border: '#bbf7d0' },
  { color: '#991b1b', bg: '#fee2e2', border: '#fecaca' },
  { color: '#6b21a8', bg: '#f3e8ff', border: '#e9d5ff' },
  { color: '#92400e', bg: '#fef3c7', border: '#fde68a' },
  { color: '#0c4a6e', bg: '#e0f2fe', border: '#bae6fd' },
  { color: '#134e4a', bg: '#ccfbf1', border: '#99f6e4' },
];

const AVATAR_COLORS = [
  '#0f1f5c', '#059669', '#7c3aed', '#dc2626',
  '#d97706', '#0891b2', '#be185d', '#16a34a',
  '#9333ea', '#0369a1', '#b45309', '#047857',
];

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

const todayISO = () => new Date().toISOString().split('T')[0];

const getInitials = (name = '') =>
  name.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase() || '?';

const getAvatarColor = (name = '') => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

const getAreaNames = (row) => {
  const areas = row?.enquiry?.areas;
  if (Array.isArray(areas) && areas.length > 0)
    return areas.map((a) => a.areaName || a.area || a.name || '').filter(Boolean).join(', ');
  return row?.customer?.city || '—';
};

const buildStatusMap = (list) => {
  const map = {};
  list.forEach((s, i) => {
    const pal = STATUS_PALETTE[i % STATUS_PALETTE.length];
    const label = s.leadstatusName || s.status_name || s.statusName || s.name || '';
    const id = String(s.leadstatusId || s.status_id || s.statusId || s.id || i);
    const value = label.toLowerCase().replace(/\s+/g, '_');
    const entry = { ...pal, label, value, id };
    map[id] = entry;
    map[value] = entry;
    map[label.toLowerCase()] = entry;
  });
  return map;
};

const resolveStatus = (status, statusMap) => {
  if (!status) return { color: '#374151', bg: '#f3f4f6', border: '#e5e7eb', label: 'Default' };
  const key = String(status).toLowerCase().replace(/\s+/g, '_');
  return (
    statusMap[String(status)] ||
    statusMap[key] ||
    statusMap[status.toLowerCase()] ||
    { color: '#374151', bg: '#f3f4f6', border: '#e5e7eb', label: status }
  );
};

const getMediaLabel = (media, mediaId, commMedia) => {
  if (media) return media.communicationmediaName || media.communicationMedia || media.name || '—';
  const m = commMedia.find((c) => (c.communicationmediaId || c.id) == mediaId);
  return m ? (m.communicationmediaName || m.communicationMedia || m.name || '—') : String(mediaId || '—');
};

// ─── Styles ───────────────────────────────────────────────────────────────────
const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px', bgcolor: '#ffffff',
    fontSize: '0.88rem', fontFamily: FONT, fontWeight: 500,
    color: LABEL,
    '& fieldset': { borderColor: '#cbd5e1', borderWidth: '1.5px' },
    '&:hover fieldset': { borderColor: '#6366f1' },
    '&.Mui-focused fieldset': { borderColor: NAVY, borderWidth: '2px' },
  },
};

const ColLabel = ({ children }) => (
  <Typography sx={{ fontFamily: FONT, fontSize: '0.72rem', fontWeight: 800, color: LABEL, letterSpacing: '1.2px', textTransform: 'uppercase', mb: 0.8 }}>
    {children}
  </Typography>
);

// ─── Media Icon Circle ────────────────────────────────────────────────────────
const MediaIconCircle = ({ label, size = 32 }) => {
  const key = (label || '').toLowerCase().replace(/\s/g, '');
  const color = MEDIA_CIRCLE_COLORS[key] || '#64748b';
  const icon = MEDIA_ICON_MAP[key] || <PhoneEnabled />;
  return (
    <Box sx={{ width: size, height: size, borderRadius: '50%', bgcolor: color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: `0 2px 8px ${color}55` }}>
      {React.cloneElement(icon, { sx: { fontSize: size * 0.5, color: '#fff' } })}
    </Box>
  );
};

// ─── Chips ────────────────────────────────────────────────────────────────────
const StatusChip = ({ status, statusMap = {} }) => {
  const s = resolveStatus(status, statusMap);
  return (
    <Chip size="small" label={s.label}
      icon={<FiberManualRecord sx={{ fontSize: '8px !important', color: `${s.color} !important`, ml: '8px !important' }} />}
      sx={{ bgcolor: s.bg, color: s.color, fontWeight: 700, fontSize: '0.78rem', fontFamily: FONT, border: `1.5px solid ${s.border || s.bg}`, height: 28, px: 0.5 }} />
  );
};

const PriorityChip = ({ priority }) => {
  const p = getPriorityOption(priority);
  return (
    <Chip size="small" label={p.label}
      icon={React.cloneElement(p.icon, { style: { color: p.color, marginLeft: 6, fontSize: 14 } })}
      sx={{ bgcolor: p.bg, color: p.color, fontWeight: 700, fontSize: '0.78rem', fontFamily: FONT, border: `1.5px solid ${p.border}`, height: 28, px: 0.3 }} />
  );
};

const MediaPill = ({ media, mediaId, commMedia }) => {
  const label = getMediaLabel(media, mediaId, commMedia);
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <MediaIconCircle label={label} size={24} />
      <Typography sx={{ fontFamily: FONT, fontSize: '0.82rem', fontWeight: 700, color: LABEL }}>{label}</Typography>
    </Box>
  );
};

// ─── Searchable Dropdown (Filter bar) ────────────────────────────────────────
const SearchableDropdown = React.memo(({ value, onChange, options, placeholder, icon, getOptionId, getOptionLabel }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const anchorRef = useRef(null);

  const filtered = options.filter((o) => getOptionLabel(o).toLowerCase().includes(search.toLowerCase()));
  const selected = options.find((o) => String(getOptionId(o)) === String(value));

  const handleToggle = () => { setOpen((prev) => { if (prev) setSearch(''); return !prev; }); };
  const handleClose = () => { setOpen(false); setSearch(''); };
  const handleClear = (e) => { e.stopPropagation(); onChange(''); setOpen(false); setSearch(''); };
  const handleSelect = (id) => { onChange(id); setOpen(false); setSearch(''); };

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <Box sx={{ position: 'relative', width: '100%' }}>
        <Box ref={anchorRef} onClick={handleToggle}
          sx={{ display: 'flex', alignItems: 'center', gap: 1, border: `1.5px solid ${open ? NAVY : '#cbd5e1'}`, borderRadius: '10px', px: 1.5, bgcolor: '#fff', cursor: 'pointer', height: 40, '&:hover': { borderColor: '#6366f1' }, transition: 'border-color 0.15s', userSelect: 'none' }}>
          {icon && React.cloneElement(icon, { sx: { fontSize: 16, color: open ? NAVY : LABEL, flexShrink: 0 } })}
          <Typography sx={{ fontFamily: FONT, fontSize: '0.88rem', fontWeight: selected ? 600 : 400, color: LABEL, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {selected ? getOptionLabel(selected) : placeholder}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {value && <IconButton size="small" onClick={handleClear} sx={{ p: 0.3, color: LABEL, '&:hover': { color: '#ef4444' } }}><Clear sx={{ fontSize: 14 }} /></IconButton>}
            <ExpandMore sx={{ fontSize: 18, color: LABEL, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
          </Box>
        </Box>
        <Popper open={open} anchorEl={anchorRef.current} placement="bottom-start" style={{ zIndex: 1400, width: anchorRef.current?.offsetWidth, minWidth: 200 }}>
          <Box sx={{ bgcolor: '#fff', borderRadius: '12px', boxShadow: '0 8px 32px rgba(15,31,92,0.18)', border: '1.5px solid #e2e8f0', overflow: 'hidden', mt: 0.5 }}>
            <Box sx={{ p: 1.2, borderBottom: '1px solid #f1f5f9' }}>
              <TextField size="small" fullWidth autoFocus placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 16, color: LABEL }} /></InputAdornment> }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: '0.85rem', fontFamily: FONT, color: LABEL } }} />
            </Box>
            <Box sx={{ maxHeight: 220, overflowY: 'auto' }}>
              <Box onClick={() => handleSelect('')} sx={{ px: 2, py: 1.2, cursor: 'pointer', bgcolor: !value ? '#f0f4ff' : 'transparent', '&:hover': { bgcolor: '#f8faff' }, fontFamily: FONT, fontSize: '0.88rem', color: LABEL, fontWeight: 500 }}>
                {placeholder}
              </Box>
              {filtered.length === 0 ? (
                <Box sx={{ px: 2, py: 2, textAlign: 'center', color: LABEL, fontFamily: FONT, fontSize: '0.85rem' }}>No results</Box>
              ) : filtered.map((o) => {
                const id = getOptionId(o); const label = getOptionLabel(o); const isSelected = String(id) === String(value);
                return (
                  <Box key={id} onClick={() => handleSelect(id)}
                    sx={{ px: 2, py: 1.2, cursor: 'pointer', bgcolor: isSelected ? '#eef2ff' : 'transparent', '&:hover': { bgcolor: '#f0f4ff' }, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography sx={{ fontFamily: FONT, fontSize: '0.88rem', fontWeight: isSelected ? 700 : 500, color: isSelected ? NAVY : LABEL }}>{label}</Typography>
                    {isSelected && <Done sx={{ fontSize: 15, color: NAVY }} />}
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
});

// ─── Modal Searchable Select ──────────────────────────────────────────────────
const SearchableSelect = ({ value, onChange, options, placeholder, renderOption, renderValue, label }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const anchorRef = useRef(null);

  const filtered = options.filter((o) =>
    JSON.stringify(o).toLowerCase().includes(search.toLowerCase())
  );

  const handleClose = () => { setOpen(false); setSearch(''); };
  const handleToggle = () => setOpen((p) => { if (p) setSearch(''); return !p; });

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <Box sx={{ position: 'relative', width: '100%' }}>
        {label && <ColLabel>{label}</ColLabel>}
        <Box ref={anchorRef} onClick={handleToggle}
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', border: `1.5px solid ${open ? NAVY : '#cbd5e1'}`, borderRadius: '10px', px: 1.5, bgcolor: '#fff', cursor: 'pointer', height: 48, '&:hover': { borderColor: '#6366f1' }, transition: 'border-color 0.15s', userSelect: 'none' }}>
          <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', alignItems: 'center' }}>
            {value !== '' && value !== undefined && value !== null
              ? renderValue(value)
              : <Typography sx={{ fontFamily: FONT, fontSize: '0.88rem', color: '#9ca3af', fontWeight: 400 }}>{placeholder}</Typography>
            }
          </Box>
          <ExpandMore sx={{ fontSize: 18, color: LABEL, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', flexShrink: 0, ml: 1 }} />
        </Box>
        <Popper open={open} anchorEl={anchorRef.current} placement="bottom-start"
          style={{ zIndex: 1500, width: anchorRef.current?.offsetWidth, minWidth: 200 }}>
          <Box sx={{ bgcolor: '#fff', borderRadius: '14px', boxShadow: '0 12px 40px rgba(15,31,92,0.18)', border: '1.5px solid #e2e8f0', overflow: 'hidden', mt: 0.5 }}>
            <Box sx={{ p: 1.2, borderBottom: '1px solid #f1f5f9' }}>
              <TextField size="small" fullWidth autoFocus placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 16, color: '#9ca3af' }} /></InputAdornment> }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: '0.85rem', fontFamily: FONT, color: LABEL, bgcolor: '#f8fafc', '& fieldset': { borderColor: '#e2e8f0' }, '&:hover fieldset': { borderColor: '#cbd5e1' }, '&.Mui-focused fieldset': { borderColor: NAVY } } }}
              />
            </Box>
            
            <Box sx={{ maxHeight: 260, overflowY: 'auto' }}>
              {filtered.length === 0 ? (
                <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
                  <Typography sx={{ fontFamily: FONT, fontSize: '0.85rem', color: '#9ca3af' }}>No results found</Typography>
                </Box>
              ) : filtered.map((o, i) =>
                renderOption(o, i, value, (val) => { onChange(val); handleClose(); })
              )}
            </Box>
          </Box>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};

// ─── Modal Header ─────────────────────────────────────────────────────────────
const ModalHeader = ({ icon, title, subtitle, onClose, accentColor = ORANGE }) => (
  <Box sx={{ px: { xs: 2, sm: 4 }, py: { xs: 2, sm: 3 }, bgcolor: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid #f1f5f9`, position: 'relative' }}>
    <Box sx={{ position: 'absolute', left: 0, top: 12, bottom: 12, width: 4, bgcolor: accentColor, borderRadius: '0 4px 4px 0' }} />
    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2 }, pl: { xs: 1, sm: 1.5 } }}>
      <Box sx={{ width: { xs: 36, sm: 44 }, height: { xs: 36, sm: 44 }, borderRadius: '12px', bgcolor: `${NAVY}0d`, display: 'flex', alignItems: 'center', justifyContent: 'center', border: `1.5px solid ${NAVY}18`, flexShrink: 0 }}>
        {React.cloneElement(icon, { sx: { color: NAVY, fontSize: { xs: 17, sm: 20 } } })}
      </Box>
      <Box>
        <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: { xs: '1rem', sm: '1.2rem' }, color: LABEL, lineHeight: 1.2 }}>{title}</Typography>
        <Typography sx={{ fontFamily: FONT, fontSize: { xs: '0.75rem', sm: '0.82rem' }, color: LABEL, mt: 0.2, fontWeight: 500 }}>{subtitle}</Typography>
      </Box>
    </Box>
    <IconButton onClick={onClose} sx={{ bgcolor: '#f8fafc', borderRadius: '10px', color: LABEL, border: '1.5px solid #e2e8f0', '&:hover': { bgcolor: '#fee2e2', color: '#ef4444', borderColor: '#fecaca' }, transition: 'all 0.15s', flexShrink: 0 }}>
      <Close sx={{ fontSize: 18 }} />
    </IconButton>
  </Box>
);

// ─── Customer Strip ───────────────────────────────────────────────────────────
const CustomerStrip = ({ cust, status, statusMap, extra }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2.5 }, p: { xs: 1.8, sm: 2.5 }, mb: 3, borderRadius: '14px', border: '1.5px solid #e8edf5', background: 'linear-gradient(135deg, #f8faff 0%, #f0f4ff 100%)' }}>
    <Avatar sx={{ bgcolor: NAVY, width: { xs: 42, sm: 52 }, height: { xs: 42, sm: 52 }, fontSize: { xs: '0.95rem', sm: '1.1rem' }, fontFamily: FONT, fontWeight: 800, border: `2.5px solid ${ORANGE}`, flexShrink: 0, boxShadow: `0 4px 14px ${NAVY}30` }}>
      {getInitials(cust.customerName)}
    </Avatar>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: { xs: '0.95rem', sm: '1.1rem' }, color: LABEL }}>{cust.customerName || '—'}</Typography>
      <Typography sx={{ fontFamily: FONT, fontSize: { xs: '0.78rem', sm: '0.85rem' }, color: LABEL, fontWeight: 500, mt: 0.2 }}>{[cust.mobileno, cust.city].filter(Boolean).join('  ·  ')}</Typography>
    </Box>
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.8, flexShrink: 0 }}>
      <StatusChip status={status} statusMap={statusMap} />
      {extra}
    </Box>
  </Box>
);

// ─── History Table (scrollable on mobile) ────────────────────────────────────
const HistoryTable = ({ leadHistory, commMedia, employees, statusMap }) => {
  return leadHistory.length === 0 ? (
    <Box sx={{ textAlign: 'center', py: 5, borderRadius: '12px', border: '1.5px dashed #e2e8f0' }}>
      <History sx={{ fontSize: 32, color: '#e2e8f0', mb: 1 }} />
      <Typography sx={{ fontFamily: FONT, fontSize: '0.88rem', color: LABEL, fontWeight: 500 }}>No communication history yet</Typography>
    </Box>
  ) : (
    <Box sx={{ borderRadius: '12px', border: '1.5px solid #e8edf5', overflow: 'hidden', boxShadow: '0 2px 8px rgba(15,31,92,0.04)' }}>
      <Box sx={{ maxHeight: 280, overflowY: 'auto', overflowX: 'auto' }}>
        <Table size="small" sx={{ tableLayout: 'fixed', minWidth: 560 }}>
          <TableHead>
            <TableRow>
              {[
                { h: '#', w: 36 },
                { h: 'Date', w: 90 },
                { h: 'Media', w: 90 },
                { h: 'Status', w: 100 },
                { h: 'Priority', w: 80 },
                { h: 'Assigned To', w: 110 },
                { h: 'Note', w: 110 },
                { h: 'Active', w: 60 },
              ].map(({ h, w }) => (
                <TableCell key={h} sx={{ width: w, bgcolor: '#f8fafc', fontFamily: FONT, fontWeight: 800, color: LABEL, fontSize: '0.64rem', letterSpacing: '0.8px', textTransform: 'uppercase', py: 1.5, borderBottom: '1.5px solid #e8edf5', position: 'sticky', top: 0, zIndex: 1 }}>
                  {h}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {leadHistory.map((h, i) => (
              <TableRow key={h.id || i} sx={{ bgcolor: h.isActive === 0 ? '#fafafa' : '#fff', opacity: h.isActive === 0 ? 0.75 : 1, '&:hover': { bgcolor: h.isActive === 0 ? '#f1f5f9' : '#f8faff' }, '&:last-child td': { borderBottom: 0 } }}>
                <TableCell sx={{ fontFamily: FONT, fontSize: '0.78rem', color: LABEL, fontWeight: 600, py: 1.5 }}>{i + 1}</TableCell>
                <TableCell sx={{ py: 1.5 }}>
                  <Typography sx={{ fontFamily: FONT, fontSize: '0.78rem', color: LABEL, fontWeight: 600, lineHeight: 1.3 }}>{h.datePart || '—'}</Typography>
                </TableCell>
                <TableCell sx={{ py: 1.5 }}><MediaPill media={h.media} mediaId={h.mediaId} commMedia={commMedia} /></TableCell>
                <TableCell sx={{ py: 1.5 }}><StatusChip status={h.status} statusMap={statusMap} /></TableCell>
                <TableCell sx={{ py: 1.5 }}><PriorityChip priority={h.priority} /></TableCell>
                <TableCell sx={{ py: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar sx={{ width: 24, height: 24, fontSize: '0.62rem', bgcolor: getAvatarColor(h.assignedName), fontFamily: FONT, flexShrink: 0 }}>{getInitials(h.assignedName)}</Avatar>
                    <Typography sx={{ fontFamily: FONT, fontSize: '0.78rem', fontWeight: 600, color: LABEL, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{h.assignedName || '—'}</Typography>
                  </Box>
                </TableCell>
                <TableCell sx={{ py: 1.5 }}>
                  <Tooltip title={h.note} arrow>
                    <Typography sx={{ fontFamily: FONT, fontSize: '0.78rem', color: LABEL, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{h.note || '—'}</Typography>
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ py: 1.5 }}>
                  <Chip size="small" label={h.isActive === 1 ? 'Active' : 'Old'} sx={{ height: 20, fontSize: '0.62rem', fontWeight: 800, fontFamily: FONT, bgcolor: h.isActive === 1 ? '#dcfce7' : '#f1f5f9', color: h.isActive === 1 ? '#166534' : LABEL }} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
};

// ─── Helper: DB followup row → history row ────────────────────────────────────
const mapFollowupToHistory = (d, employees) => {
  const rawDateTime = d.callLeadDate || d.lastChanged;
  const dt = rawDateTime ? new Date(rawDateTime) : null;
  const datePart = dt && !isNaN(dt) ? dt.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
  const timePart = dt && !isNaN(dt) ? dt.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }) : '';
  return {
    id: d.followupId, datePart, timePart, rawDateTime,
    mediaId: d.fkcommunicationmediaId,
    media: d.media || null,
    status: d.fkLeadStatusId ? String(d.fkLeadStatusId) : '',
    priority: normP(d.priority),
    assignedName: (employees.find((e) => e.employeeId == d.assignTo) || {}).employeeName || '—',
    note: d.followupRemark || '',
    isActive: d.isActive ?? 1,
  };
};

// ─── Info Modal ───────────────────────────────────────────────────────────────
const InfoModal = ({ open, onClose, lead, commMedia, employees, statusMap }) => {
  const [leadHistory, setLeadHistory] = useState([]);
  const [histLoading, setHistLoading] = useState(false);

  useEffect(() => {
    if (!open || !lead) return;
    const enquiryId = lead.fkenquiryId;
    if (!enquiryId) return;
    setHistLoading(true);
    fetch(`${URL}/followup-masters?filter=${encodeURIComponent(JSON.stringify({ where: { fkenquiryId: enquiryId }, order: ['followupId DESC'] }))}`)
      .then((r) => r.json())
      .then((data) => { if (!Array.isArray(data)) return; setLeadHistory(data.map((d) => mapFollowupToHistory(d, employees))); })
      .catch(() => setLeadHistory([]))
      .finally(() => setHistLoading(false));
  }, [open, lead?.fkenquiryId]);

  if (!lead) return null;
  const cust = lead.enquiry?.customer || lead.customer || {};

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth
      PaperProps={{ sx: { borderRadius: { xs: '16px', sm: '20px' }, overflow: 'hidden', fontFamily: FONT, boxShadow: '0 24px 80px rgba(15,31,92,0.18)', minHeight: '60vh', maxWidth: '1100px', mx: { xs: 1, sm: 2 } } }}>
      <ModalHeader icon={<Visibility />} title={cust.customerName || 'Lead Details'} subtitle="Communication History" onClose={onClose} />
      <DialogContent sx={{ p: 0, bgcolor: '#fafbff' }}>
        <Box sx={{ px: { xs: 2, sm: 4 }, pt: 3, pb: 2 }}>
          {(() => {
            const emp = employees.find(e => e.employeeId == lead.assignTo);
            const rows = [
              { label: 'Customer', value: cust.customerName, color: NAVY },
              { label: 'Mobile', value: cust.mobileno, color: '#0369a1' },
              { label: 'Area', value: lead.enquiry?.areas?.length > 0 ? lead.enquiry.areas.map(a => a.areaName).filter(Boolean).join(', ') : (cust.city || null), color: '#065f46' },
              { label: 'Communication', value: 'MEDIA', color: '#6366f1' },
              { label: 'Priority', value: 'PRIORITY', color: '#b45309' },
              { label: 'Last Contact', value: fmtDate(lead.callLeadDate), color: '#92400e' },
              { label: 'Assigned To', value: emp ? emp.employeeName : null, color: '#166534' },
              { label: 'Status', value: 'STATUS', color: '#6d28d9' },
            ];
            return rows.filter(r => r.value).map((row, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'center', py: 1.1, px: { xs: 1.5, sm: 2 }, borderLeft: `3px solid ${row.color}`, borderRadius: '0 10px 10px 0', mb: 0.8, bgcolor: `${row.color}08` }}>
                <Typography sx={{ fontFamily: FONT, fontSize: '0.78rem', fontWeight: 800, color: row.color, width: { xs: 110, sm: 140 }, flexShrink: 0 }}>{row.label}</Typography>
                <Box sx={{ fontFamily: FONT, fontSize: '0.9rem', fontWeight: 600, color: LABEL }}>
                  {row.value === 'MEDIA' ? <MediaPill media={lead.media} mediaId={lead.fkcommunicationmediaId} commMedia={commMedia} />
                    : row.value === 'PRIORITY' ? <PriorityChip priority={normP(lead.priority)} />
                      : row.value === 'STATUS' ? <StatusChip status={lead.fkLeadStatusId ? String(lead.fkLeadStatusId) : lead?.leadStatus} statusMap={statusMap} />
                        : row.value}
                </Box>
              </Box>
            ));
          })()}
        </Box>
        <Box sx={{ px: { xs: 2, sm: 4 }, pb: 3.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <Box sx={{ width: 3, height: 18, bgcolor: ORANGE, borderRadius: '3px' }} />
            <History sx={{ fontSize: 16, color: LABEL }} />
            <Typography sx={{ fontFamily: FONT, fontSize: '0.78rem', fontWeight: 800, color: LABEL, letterSpacing: '1px', textTransform: 'uppercase' }}>Communication History</Typography>
            {histLoading
              ? <CircularProgress size={13} sx={{ color: NAVY }} />
              : <Chip label={leadHistory.length} size="small" sx={{ bgcolor: `${NAVY}0d`, color: NAVY, fontFamily: FONT, fontWeight: 800, fontSize: '0.75rem' }} />}
          </Box>
          {histLoading
            ? <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress size={28} sx={{ color: NAVY }} /></Box>
            : <HistoryTable leadHistory={leadHistory} commMedia={commMedia} employees={employees} statusMap={statusMap} />}
        </Box>
      </DialogContent>
      <Box sx={{ px: { xs: 2, sm: 4 }, py: 2.5, borderTop: '1px solid #f1f5f9', bgcolor: '#fff', display: 'flex', justifyContent: 'flex-end' }}>
        <Button onClick={onClose} variant="outlined" size="large"
          sx={{ borderRadius: '10px', textTransform: 'none', fontFamily: FONT, fontWeight: 700, fontSize: '0.95rem', px: 5, color: LABEL, borderColor: '#cbd5e1', '&:hover': { borderColor: '#94a3b8', bgcolor: '#f8fafc' } }}>
          Close
        </Button>
      </Box>
    </Dialog>
  );
};

// ─── Follow-Up Modal ──────────────────────────────────────────────────────────
const FollowUpModal = ({ open, onClose, lead, onSave, commMedia, employees, statusOptions, statusMap }) => {
  const defaultStatus = statusOptions.length > 0 ? (statusOptions[0].value || statusOptions[0].id || '') : '';

  const [form, setForm] = useState({ status: defaultStatus, mediaId: '', assignedTo: '', leadDate: todayISO(), priority: 2, note: '' });
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState('');
  const [leadHistory, setLeadHistory] = useState([]);
  const [histLoading, setHistLoading] = useState(false);

  useEffect(() => {
    if (!open || !lead) return;
    setForm({
      status: lead.fkLeadStatusId ? String(lead.fkLeadStatusId) : defaultStatus,
      mediaId: String(lead.fkcommunicationmediaId || commMedia[0]?.communicationmediaId || commMedia[0]?.id || ''),
      assignedTo: String(lead.assignTo || employees[0]?.employeeId || ''),
      leadDate: lead.callLeadDate ? lead.callLeadDate.split('T')[0] : todayISO(),
      priority: getPriorityOption(normP(lead.priority)).value,
      note: '',
    });
    setSaveErr('');
    const enquiryId = lead.fkenquiryId;
    if (!enquiryId) return;
    setHistLoading(true);
    fetch(`${URL}/followup-masters?filter=${encodeURIComponent(JSON.stringify({ where: { fkenquiryId: enquiryId }, order: ['followupId DESC'] }))}`)
      .then((r) => r.json())
      .then((data) => { if (!Array.isArray(data)) return; setLeadHistory(data.map((d) => mapFollowupToHistory(d, employees))); })
      .catch(() => setLeadHistory([]))
      .finally(() => setHistLoading(false));
  }, [open, lead?.fkenquiryId]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSave = async () => {
    setSaving(true); setSaveErr('');
    try {
      const saveTimestamp = new Date();
      const now = saveTimestamp.toISOString();
      const oldFollowupId = lead._parentFollowupId || lead.followupId;

      const priorityVal = Number(form.priority);
      const payload = {
        fkenquiryId: lead.fkenquiryId,
        fkcustomerId: lead.fkcustomerId,
        fkLeadStatusId: Number(form.status) || 0,
        fkcommunicationmediaId: Number(form.mediaId) || undefined,
        assignTo: Number(form.assignedTo) || undefined,
        followupPerson: Number(form.assignedTo) || undefined,
        priority: priorityVal > 0 ? priorityVal : 2,
        callLeadTime: saveTimestamp.toTimeString().slice(0, 5),
        followupRemark: form.note || undefined,
        callLeadDate: form.leadDate
          ? new Date(`${form.leadDate}T${saveTimestamp.getHours()}:${String(saveTimestamp.getMinutes()).padStart(2, '0')}:${String(saveTimestamp.getSeconds()).padStart(2, '0')}`).toISOString()
          : now,
        isActive: 1,
        lastChanged: now,
      };

      if (oldFollowupId) {
        await fetch(`${URL}/followup-masters/${oldFollowupId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ isActive: 0, nextFollowupDate: payload.callLeadDate }),
        });
      }

      const res = await fetch(`${URL}/followup-masters`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`Server error: ${res.status}`);
      const saved = await res.json();

      const emp = employees.find((e) => e.employeeId == form.assignedTo);
      const selectedMedia = commMedia.find((c) => String(c.communicationmediaId || c.id) === String(form.mediaId)) || null;

      onSave(lead, {
        ...form,
        assignedName: emp ? emp.employeeName : '—',
        savedAt: now,
        leadDate: form.leadDate,
        id: saved?.followupId || Date.now(),
        savedToDb: true,
        media: selectedMedia,
        mediaId: form.mediaId,
        oldFollowupId,
        priority: Number(form.priority) || 2,
      });
      onClose();
    } catch (e) {
      setSaveErr('Save failed: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  const cust = lead?.enquiry?.customer || lead?.customer || {};

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth
      PaperProps={{ sx: { borderRadius: { xs: '16px', sm: '20px' }, overflow: 'hidden', fontFamily: FONT, boxShadow: '0 24px 80px rgba(15,31,92,0.18)', maxWidth: '1100px', mx: { xs: 1, sm: 2 } } }}>
      <ModalHeader icon={<ArrowForward />} title="Log Follow-Up" subtitle="Update lead status and schedule next action" onClose={onClose} accentColor={NAVY} />

      <DialogContent sx={{ p: { xs: 2, sm: 4 }, bgcolor: '#fafbff' }}>
        <CustomerStrip cust={cust} status={lead?.fkLeadStatusId ? String(lead.fkLeadStatusId) : lead?.leadStatus} statusMap={statusMap} />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
          <Box sx={{ width: 3, height: 18, bgcolor: ORANGE, borderRadius: '3px' }} />
          <Typography sx={{ fontFamily: FONT, fontSize: '0.72rem', fontWeight: 800, color: LABEL, letterSpacing: '1px', textTransform: 'uppercase' }}>Follow-Up Details</Typography>
        </Box>

        <Box sx={{ bgcolor: '#fff', borderRadius: '14px', border: '1.5px solid #e8edf5', p: { xs: 2, sm: 3 }, mb: 3, boxShadow: '0 1px 6px rgba(15,31,92,0.04)' }}>
          <Grid container spacing={2.5}>
            <Grid item xs={12} sm={6} md={4}>
              <SearchableSelect label="Status" value={form.status} onChange={(val) => setForm((f) => ({ ...f, status: val }))} options={statusOptions} placeholder="Select status..."
                renderValue={(val) => {
                  const pal = resolveStatus(val, statusMap);
                  const opt = statusOptions.find((o) => String(o.value || o.id) === String(val));
                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: pal.color, flexShrink: 0 }} />
                      <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: '0.93rem', color: LABEL }}>{opt?.label || pal.label || val}</Typography>
                    </Box>
                  );
                }}
                renderOption={(o, i, currentVal, onSelect) => {
                  const val = String(o.value || o.id || '');
                  const pal = resolveStatus(val, statusMap);
                  const isSelected = val === String(currentVal);
                  return (
                    <Box key={val} onClick={() => onSelect(val)} sx={{ px: 2, py: 1.3, cursor: 'pointer', bgcolor: isSelected ? '#eef2ff' : 'transparent', '&:hover': { bgcolor: '#f8faff' }, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: pal.color, flexShrink: 0 }} />
                      <Typography sx={{ fontFamily: FONT, fontWeight: isSelected ? 700 : 500, fontSize: '0.93rem', color: isSelected ? NAVY : LABEL, flex: 1 }}>{o.label}</Typography>
                      {isSelected && <Done sx={{ fontSize: 15, color: NAVY }} />}
                    </Box>
                  );
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <SearchableSelect label="Communication Media" value={form.mediaId} onChange={(val) => setForm((f) => ({ ...f, mediaId: val }))} options={commMedia} placeholder="Select media..."
                renderValue={(val) => {
                  const m = commMedia.find((c) => String(c.communicationmediaId || c.id) === String(val));
                  const label = m ? (m.communicationmediaName || m.communicationMedia || m.name || '—') : '—';
                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <MediaIconCircle label={label} size={28} />
                      <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: '0.93rem', color: LABEL }}>{label}</Typography>
                    </Box>
                  );
                }}
                renderOption={(o, i, currentVal, onSelect) => {
                  const id = String(o.communicationmediaId || o.id);
                  const label = o.communicationmediaName || o.communicationMedia || o.name || '—';
                  const isSelected = id === String(currentVal);
                  return (
                    <Box key={id} onClick={() => onSelect(id)} sx={{ px: 2, py: 1.3, cursor: 'pointer', bgcolor: isSelected ? '#eef2ff' : 'transparent', '&:hover': { bgcolor: '#f8faff' }, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <MediaIconCircle label={label} size={32} />
                      <Typography sx={{ fontFamily: FONT, fontWeight: isSelected ? 700 : 500, fontSize: '0.93rem', color: isSelected ? NAVY : LABEL, flex: 1 }}>{label}</Typography>
                      {isSelected && <Done sx={{ fontSize: 15, color: NAVY }} />}
                    </Box>
                  );
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <SearchableSelect label="Priority" value={form.priority} onChange={(val) => setForm((f) => ({ ...f, priority: Number(val) }))} options={PRIORITY_OPTIONS} placeholder="Select priority..."
                renderValue={(val) => {
                  const opt = getPriorityOption(val);
                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box sx={{ width: 30, height: 30, borderRadius: '50%', bgcolor: opt.bg, border: `2px solid ${opt.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        {React.cloneElement(opt.icon, { style: { color: opt.color, fontSize: 16 } })}
                      </Box>
                      <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: '0.93rem', color: opt.color }}>{opt.label}</Typography>
                    </Box>
                  );
                }}
                renderOption={(o, i, currentVal, onSelect) => {
                  const isSelected = String(o.value) === String(currentVal);
                  return (
                    <Box key={o.value} onClick={() => onSelect(o.value)} sx={{ px: 2, py: 1.3, cursor: 'pointer', bgcolor: isSelected ? '#eef2ff' : 'transparent', '&:hover': { bgcolor: '#f8faff' }, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box sx={{ width: 32, height: 32, borderRadius: '50%', bgcolor: o.bg, border: `2px solid ${o.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        {React.cloneElement(o.icon, { style: { color: o.color, fontSize: 18 } })}
                      </Box>
                      <Typography sx={{ fontFamily: FONT, fontWeight: isSelected ? 700 : 500, fontSize: '0.93rem', color: isSelected ? NAVY : LABEL, flex: 1 }}>{o.label}</Typography>
                      <Chip label={o.value === 1 ? 'Urgent' : o.value === 2 ? 'Normal' : 'Deferred'} size="small"
                        sx={{ height: 20, fontSize: '0.62rem', bgcolor: o.bg, color: o.color, fontFamily: FONT, fontWeight: 700, border: `1px solid ${o.border}` }} />
                      {isSelected && <Done sx={{ fontSize: 15, color: NAVY, ml: 0.5 }} />}
                    </Box>
                  );
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <SearchableSelect label="Assigned To" value={form.assignedTo} onChange={(val) => setForm((f) => ({ ...f, assignedTo: val }))} options={employees} placeholder="Select employee..."
                renderValue={(val) => {
                  const emp = employees.find((e) => String(e.employeeId) === String(val));
                  if (!emp) return <Typography sx={{ fontFamily: FONT, fontSize: '0.88rem', color: '#9ca3af' }}>Select employee</Typography>;
                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar sx={{ width: 30, height: 30, fontSize: '0.66rem', bgcolor: getAvatarColor(emp.employeeName), fontFamily: FONT, fontWeight: 800, flexShrink: 0 }}>{getInitials(emp.employeeName)}</Avatar>
                      <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: '0.93rem', color: LABEL }}>{emp.employeeName}</Typography>
                    </Box>
                  );
                }}
                renderOption={(o, i, currentVal, onSelect) => {
                  const id = String(o.employeeId);
                  const isSelected = id === String(currentVal);
                  return (
                    <Box key={id} onClick={() => onSelect(id)} sx={{ px: 2, py: 1.3, cursor: 'pointer', bgcolor: isSelected ? '#eef2ff' : 'transparent', '&:hover': { bgcolor: '#f8faff' }, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar sx={{ width: 34, height: 34, fontSize: '0.7rem', bgcolor: getAvatarColor(o.employeeName), fontFamily: FONT, fontWeight: 800, flexShrink: 0 }}>{getInitials(o.employeeName)}</Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontFamily: FONT, fontWeight: isSelected ? 700 : 600, fontSize: '0.93rem', color: isSelected ? NAVY : LABEL }}>{o.employeeName}</Typography>
                        {o.designation && <Typography sx={{ fontFamily: FONT, fontSize: '0.72rem', color: '#6b7280', lineHeight: 1.2 }}>{o.designation}</Typography>}
                      </Box>
                      {isSelected && <Done sx={{ fontSize: 15, color: NAVY }} />}
                    </Box>
                  );
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <ColLabel>Next Follow-Up Date</ColLabel>
              <TextField type="date" fullWidth value={form.leadDate} onChange={set('leadDate')}
                InputProps={{ startAdornment: <InputAdornment position="start"><CalendarToday sx={{ fontSize: 17, color: NAVY }} /></InputAdornment>, sx: { height: 48, color: LABEL } }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', bgcolor: '#fff', fontFamily: FONT, fontSize: '0.93rem', fontWeight: 500, color: LABEL, '& fieldset': { borderColor: '#cbd5e1', borderWidth: '1.5px' }, '&:hover fieldset': { borderColor: '#6366f1' }, '&.Mui-focused fieldset': { borderColor: NAVY, borderWidth: '2px' } } }}
              />
            </Grid>

            <Grid item xs={12}>
              <ColLabel>Note & Remark</ColLabel>
              <TextField multiline rows={4} fullWidth value={form.note} onChange={set('note')}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', bgcolor: '#fff', fontFamily: FONT, fontSize: '0.93rem', fontWeight: 500, '& fieldset': { borderColor: '#cbd5e1', borderWidth: '1.5px' }, '&:hover fieldset': { borderColor: '#6366f1' }, '&.Mui-focused fieldset': { borderColor: NAVY, borderWidth: '2px' }, '& textarea': { fontFamily: FONT, fontSize: '0.93rem', lineHeight: 1.7, color: LABEL } } }}
              />
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <Box sx={{ width: 3, height: 18, bgcolor: ORANGE, borderRadius: '3px' }} />
          <History sx={{ fontSize: 17, color: LABEL }} />
          <Typography sx={{ fontFamily: FONT, fontSize: '0.92rem', fontWeight: 800, color: LABEL }}>Communication History</Typography>
          {histLoading ? <CircularProgress size={13} sx={{ color: NAVY }} /> : <Chip label={leadHistory.length} size="small" sx={{ bgcolor: `${NAVY}0d`, color: NAVY, fontFamily: FONT, fontWeight: 800, fontSize: '0.75rem' }} />}
        </Box>
        {histLoading
          ? <Box sx={{ textAlign: 'center', py: 3 }}><CircularProgress size={26} sx={{ color: NAVY }} /></Box>
          : <HistoryTable leadHistory={leadHistory} commMedia={commMedia} employees={employees} statusMap={statusMap} />}

        {saveErr && <Alert severity="error" sx={{ mt: 2, fontFamily: FONT, borderRadius: '10px' }}>{saveErr}</Alert>}
      </DialogContent>

      <Box sx={{ px: { xs: 2, sm: 4 }, py: 2.5, borderTop: '1px solid #f1f5f9', bgcolor: '#fff', display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
        <Button onClick={onClose} variant="outlined" size="large" disabled={saving}
          sx={{ borderRadius: '10px', textTransform: 'none', fontFamily: FONT, fontWeight: 700, fontSize: { xs: '0.85rem', sm: '0.95rem' }, px: { xs: 2.5, sm: 4 }, color: LABEL, borderColor: '#cbd5e1', '&:hover': { borderColor: '#94a3b8', bgcolor: '#f8fafc' } }}>
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" size="large" disabled={saving}
          startIcon={saving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <Done sx={{ fontSize: 19 }} />}
          sx={{ borderRadius: '10px', textTransform: 'none', fontFamily: FONT, fontWeight: 800, fontSize: { xs: '0.85rem', sm: '0.95rem' }, px: { xs: 2.5, sm: 5 }, background: `linear-gradient(135deg, ${NAVY}, #1d4ed8)`, boxShadow: `0 4px 16px rgba(15,31,92,0.35)`, '&:hover': { background: `linear-gradient(135deg, #0c1745, #1e3a8a)` } }}>
          {saving ? 'Saving...' : 'Save & Update'}
        </Button>
      </Box>
    </Dialog>
  );
};

// ─── Delete Confirmation Dialog ───────────────────────────────────────────────
const DeleteConfirmDialog = ({ open, onClose, onConfirm, lead, deleting }) => {
  const cust = lead?.enquiry?.customer || lead?.customer || {};
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth
      PaperProps={{ sx: { borderRadius: '20px', overflow: 'hidden', fontFamily: FONT, boxShadow: '0 24px 80px rgba(220,38,38,0.15)', mx: { xs: 2, sm: 'auto' } } }}>
      <Box sx={{ px: 3.5, pt: 3.5, pb: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
        <Box sx={{ width: 64, height: 64, borderRadius: '50%', bgcolor: '#fee2e2', border: '2px solid #fecaca', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
          <DeleteIcon sx={{ fontSize: 32, color: '#dc2626' }} />
        </Box>
        <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: '1.15rem', color: LABEL, mb: 0.8 }}>Delete Follow-Up?</Typography>
        <Typography sx={{ fontFamily: FONT, fontSize: '0.88rem', color: '#6b7280', fontWeight: 500, lineHeight: 1.6, mb: 0.5 }}>
          This follow-up record will be permanently deleted.
        </Typography>
        {cust.customerName && (
          <Box sx={{ mt: 1.5, px: 2.5, py: 1.2, bgcolor: '#fef2f2', borderRadius: '10px', border: '1.5px solid #fecaca', width: '100%' }}>
            <Typography sx={{ fontFamily: FONT, fontSize: '0.82rem', fontWeight: 800, color: '#991b1b' }}>{cust.customerName}</Typography>
            {cust.mobileno && <Typography sx={{ fontFamily: FONT, fontSize: '0.78rem', color: '#b91c1c', fontWeight: 500, mt: 0.2 }}>{cust.mobileno}</Typography>}
          </Box>
        )}
        <Typography sx={{ fontFamily: FONT, fontSize: '0.8rem', color: '#9ca3af', mt: 1.5, fontWeight: 500 }}>This action cannot be undone.</Typography>
      </Box>
      <Box sx={{ px: 3.5, py: 3, display: 'flex', gap: 1.5 }}>
        <Button fullWidth onClick={onClose} variant="outlined" disabled={deleting}
          sx={{ borderRadius: '10px', textTransform: 'none', fontFamily: FONT, fontWeight: 700, fontSize: '0.92rem', py: 1.2, color: LABEL, borderColor: '#cbd5e1', '&:hover': { borderColor: '#94a3b8', bgcolor: '#f8fafc' } }}>
          Cancel
        </Button>
        <Button fullWidth onClick={onConfirm} variant="contained" disabled={deleting}
          startIcon={deleting ? <CircularProgress size={15} sx={{ color: '#fff' }} /> : <DeleteIcon sx={{ fontSize: 17 }} />}
          sx={{ borderRadius: '10px', textTransform: 'none', fontFamily: FONT, fontWeight: 800, fontSize: '0.92rem', py: 1.2, bgcolor: '#dc2626', '&:hover': { bgcolor: '#b91c1c' }, boxShadow: '0 4px 14px rgba(220,38,38,0.35)' }}>
          {deleting ? 'Deleting...' : 'Delete'}
        </Button>
      </Box>
    </Dialog>
  );
};

// ─── Mobile Lead Card ─────────────────────────────────────────────────────────
const MobileLeadCard = ({ row, idx, page, PAGE_SIZE, commMedia, employees, statusMap, onInfo, onDelete, onFollowUp }) => {
  const cust = row.enquiry?.customer || row.customer || {};
  const areaNames = getAreaNames(row);
  const emp = employees.find((e) => e.employeeId == row.assignTo) || null;
  const isNew = row._isLocalNew;
  const rowNum = (page - 1) * PAGE_SIZE + idx + 1;
  const priorityOpt = getPriorityOption(normP(row.priority));

  return (
    <Box sx={{
      bgcolor: isNew ? '#f0fdf4' : '#fff',
      border: `1.5px solid ${isNew ? '#bbf7d0' : '#e8edf5'}`,
      borderRadius: '14px',
      p: 2,
      mb: 1.5,
      boxShadow: '0 2px 8px rgba(15,31,92,0.06)',
    }}>
      {/* Top row: avatar + name + row num + new badge */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.5 }}>
        <Avatar sx={{ bgcolor: getAvatarColor(cust.customerName || ''), width: 42, height: 42, fontSize: '0.9rem', fontFamily: FONT, fontWeight: 800, flexShrink: 0 }}>
          {getInitials(cust.customerName)}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, flexWrap: 'wrap' }}>
            <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: '0.95rem', color: LABEL }}>{cust.customerName || '—'}</Typography>
            {isNew && <Chip label="New" size="small" sx={{ height: 16, fontSize: '0.58rem', fontWeight: 800, bgcolor: '#dcfce7', color: '#166534', fontFamily: FONT, px: 0.3 }} />}
          </Box>
          <Typography sx={{ fontFamily: FONT, fontSize: '0.82rem', color: '#6b7280', fontWeight: 500 }}>{cust.mobileno || '—'}</Typography>
        </Box>
        <Typography sx={{ fontFamily: FONT, fontSize: '0.75rem', fontWeight: 700, color: '#9ca3af', flexShrink: 0 }}>#{rowNum}</Typography>
      </Box>

      {/* Info grid */}
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 1.5 }}>
        {/* Status */}
        <Box sx={{ bgcolor: '#f8fafc', borderRadius: '8px', p: 1 }}>
          <Typography sx={{ fontFamily: FONT, fontSize: '0.62rem', fontWeight: 800, color: '#9ca3af', letterSpacing: '0.8px', textTransform: 'uppercase', mb: 0.5 }}>Status</Typography>
          <StatusChip status={row.fkLeadStatusId ? String(row.fkLeadStatusId) : row.leadStatus} statusMap={statusMap} />
        </Box>
        {/* Priority */}
        <Box sx={{ bgcolor: '#f8fafc', borderRadius: '8px', p: 1 }}>
          <Typography sx={{ fontFamily: FONT, fontSize: '0.62rem', fontWeight: 800, color: '#9ca3af', letterSpacing: '0.8px', textTransform: 'uppercase', mb: 0.5 }}>Priority</Typography>
          <PriorityChip priority={normP(row.priority)} />
        </Box>
        {/* Communication */}
        <Box sx={{ bgcolor: '#f8fafc', borderRadius: '8px', p: 1 }}>
          <Typography sx={{ fontFamily: FONT, fontSize: '0.62rem', fontWeight: 800, color: '#9ca3af', letterSpacing: '0.8px', textTransform: 'uppercase', mb: 0.5 }}>Communication</Typography>
          <MediaPill media={row.media} mediaId={row.fkcommunicationmediaId} commMedia={commMedia} />
        </Box>
        {/* Last Contact */}
        <Box sx={{ bgcolor: '#f8fafc', borderRadius: '8px', p: 1 }}>
          <Typography sx={{ fontFamily: FONT, fontSize: '0.62rem', fontWeight: 800, color: '#9ca3af', letterSpacing: '0.8px', textTransform: 'uppercase', mb: 0.5 }}>Last Contact</Typography>
          <Typography sx={{ fontFamily: FONT, fontSize: '0.8rem', fontWeight: 600, color: LABEL }}>{fmtDate(row.callLeadDate)}</Typography>
          {row.nextFollowupDate && (
            <Typography sx={{ fontFamily: FONT, fontSize: '0.7rem', color: '#6b7280', mt: 0.2 }}>Next: {fmtDate(row.nextFollowupDate)}</Typography>
          )}
        </Box>
      </Box>

      {/* Area + Assigned */}
      <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
        {areaNames !== '—' && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {(row.enquiry?.areas || []).map((a) => (
              <Chip key={a.areaId} size="small" label={a.areaName}
                icon={<LocationOn sx={{ fontSize: '11px !important', color: `${NAVY} !important`, ml: '6px !important' }} />}
                sx={{ bgcolor: '#f0f4ff', color: NAVY, fontWeight: 700, fontSize: '0.72rem', fontFamily: FONT, border: `1px solid #c7d2fe`, height: 22 }} />
            ))}
            {(!row.enquiry?.areas || row.enquiry.areas.length === 0) && areaNames !== '—' && (
              <Chip size="small" label={areaNames}
                icon={<LocationOn sx={{ fontSize: '11px !important', color: `${NAVY} !important`, ml: '6px !important' }} />}
                sx={{ bgcolor: '#f0f4ff', color: NAVY, fontWeight: 700, fontSize: '0.72rem', fontFamily: FONT, border: `1px solid #c7d2fe`, height: 22 }} />
            )}
          </Box>
        )}
        {emp && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, ml: 'auto' }}>
            <Avatar sx={{ width: 22, height: 22, fontSize: '0.55rem', bgcolor: getAvatarColor(emp.employeeName), fontFamily: FONT }}>{getInitials(emp.employeeName)}</Avatar>
            <Typography sx={{ fontFamily: FONT, fontSize: '0.78rem', fontWeight: 700, color: LABEL }}>{emp.employeeName}</Typography>
          </Box>
        )}
      </Box>

      {/* Action buttons */}
      <Box sx={{ display: 'flex', gap: 1, pt: 1, borderTop: '1px solid #f1f5f9' }}>
        <Tooltip title="View Details" arrow>
          <IconButton onClick={() => onInfo(row)} size="small"
            sx={{ border: `1.5px solid ${NAVY}30`, borderRadius: '9px', color: NAVY, bgcolor: `${NAVY}08`, width: 34, height: 34, '&:hover': { bgcolor: `${NAVY}18` } }}>
            <InfoOutlined sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete" arrow>
          <IconButton onClick={() => onDelete(row)} size="small"
            sx={{ border: '1.5px solid #fecaca', borderRadius: '9px', color: '#dc2626', bgcolor: '#fff5f5', width: 34, height: 34, '&:hover': { bgcolor: '#fee2e2', borderColor: '#dc2626' } }}>
            <DeleteIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
        <Button variant="contained" size="small" onClick={() => onFollowUp(row)} fullWidth
          startIcon={<ArrowForward sx={{ fontSize: 13 }} />}
          sx={{ fontFamily: FONT, fontSize: '0.82rem', textTransform: 'none', fontWeight: 700, borderRadius: '9px', px: 1.5, py: 0.9, background: `linear-gradient(135deg, ${NAVY}, #1d4ed8)`, boxShadow: `0 2px 8px rgba(15,31,92,0.28)`, '&:hover': { background: `linear-gradient(135deg, #0c1745, #1e3a8a)` } }}>
          Follow-Up
        </Button>
      </Box>
    </Box>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const FollowUpManagement = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));   // < 600px
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));   // < 900px

  const storedUser = JSON.parse(localStorage.getItem('user_data') || '{}');
  const loggedUserId = storedUser?.userid;
  const loggedRole = storedUser?.role;
  const loggedName = storedUser?.username;

  const [rows, setRows] = useState([]);
  const [commMedia, setCommMedia] = useState([]);
  const [areas, setAreas] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [statusMap, setStatusMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [followUpOpen, setFollowUpOpen] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Mobile filter panel toggle
  const [filtersOpen, setFiltersOpen] = useState(false);

  const [page, setPage] = useState(1);
  const PAGE_SIZE = isMobile ? 10 : 5;

  const [search, setSearch] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [filterComm, setFilterComm] = useState('');
  const [filterArea, setFilterArea] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [filterNextFrom, setFilterNextFrom] = useState('');
  const [filterNextTo, setFilterNextTo] = useState('');

  const fetchAll = async () => {
    setLoading(true); setError('');
    try {
      const [fu, ar, cm, em, ls] = await Promise.all([
        fetch(`${URL}/followup-masters?filter=${encodeURIComponent(JSON.stringify({ where: { isActive: 1 } }))}`).then((r) => r.json()),
        fetch(`${URL}/area-masters`).then((r) => r.json()),
        fetch(`${URL}/communicationmedia-masters`).then((r) => r.json()),
        fetch(`${URL}/employee-masters`).then((r) => r.json()),
        fetch(`${URL}/lead-status-masters`).then((r) => r.json()).catch(() => []),
      ]);

      const fresh = Array.isArray(fu)
        ? [...fu].filter((r) => r.isActive === 1 || r.isActive === '1' || r.is_active === 1)
          .sort((a, b) => {
            const pa = normP(a.priority), pb = normP(b.priority);
            if (pa !== pb) return pa - pb;
            return (b.followupId || 0) - (a.followupId || 0);
          })
        : [];

      setRows(fresh);
      setAreas(Array.isArray(ar) ? ar : []);
      setCommMedia(Array.isArray(cm) ? cm : []);
      setEmployees(Array.isArray(em) ? em : []);

      if (Array.isArray(ls) && ls.length > 0) {
        const map = buildStatusMap(ls);
        const opts = ls.map((s, i) => {
          const pal = STATUS_PALETTE[i % STATUS_PALETTE.length];
          const label = s.leadstatusName || s.status_name || s.statusName || s.name || '';
          const value = String(s.leadstatusId || s.status_id || s.statusId || s.id || i);
          return { ...pal, label, value, id: value };
        });
        setStatusOptions(opts);
        setStatusMap(map);
      }
    } catch (e) {
      setError('Failed to connect to API: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const handleRefresh = () => {
    setSearch(''); setFromDate(''); setToDate('');
    setFilterComm(''); setFilterArea(''); setFilterPriority('');
    setFilterNextFrom(''); setFilterNextTo('');
    setPage(1); fetchAll();
  };

  useEffect(() => { setPage(1); }, [search, fromDate, toDate, filterComm, filterArea, filterPriority, filterNextFrom, filterNextTo]);

  const hasAnyFilter = search || fromDate || toDate || filterComm || filterArea || filterPriority !== '' || filterNextFrom || filterNextTo;

  const filtered = rows.filter((r) => {
    const cust = r.enquiry?.customer || r.customer || {};
    if (search) {
      const term = search.toLowerCase();
      if (!(cust.customerName || '').toLowerCase().includes(term) && !(cust.mobileno || '').includes(search)) return false;
    }
    if (fromDate || toDate) {
      const d = r.callLeadDate ? r.callLeadDate.split('T')[0] : '';
      if (fromDate && d < fromDate) return false;
      if (toDate && d > toDate) return false;
    }
    if (filterNextFrom || filterNextTo) {
      const nd = r.nextFollowupDate ? r.nextFollowupDate.split('T')[0] : '';
      if (!nd) return false;
      if (filterNextFrom && nd < filterNextFrom) return false;
      if (filterNextTo && nd > filterNextTo) return false;
    }
    if (filterComm && String(r.fkcommunicationmediaId) !== String(filterComm)) return false;
    if (filterPriority !== '' && normP(r.priority) !== Number(filterPriority)) return false;
    if (filterArea) {
      const areasList = r.enquiry?.areas;
      if (Array.isArray(areasList) && areasList.length > 0) {
        const matched = areasList.some((a) => String(a.areaId || a.areaid || a.id || a.fkAreaId) === String(filterArea));
        if (!matched) return false;
      } else {
        const directAreaId = cust.areaId || cust.fkAreaId || r.fkAreaId;
        if (String(directAreaId || '') !== String(filterArea)) return false;
      }
    }
    const matchRole = loggedRole === 1 || String(r.assignTo) === String(loggedUserId);
    if (!matchRole) return false;
    return true;
  }).sort((a, b) => {
    if (a._isLocalNew && !b._isLocalNew) return -1;
    if (!a._isLocalNew && b._isLocalNew) return 1;
    const pa = normP(a.priority), pb = normP(b.priority);
    if (pa !== pb) return pa - pb;
    return (b.followupId || 0) - (a.followupId || 0);
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleOpenFollowUp = (lead) => { setSelectedLead(lead); setFollowUpOpen(true); };
  const handleCloseFollowUp = () => { setFollowUpOpen(false); setSelectedLead(null); };
  const handleOpenInfo = (lead) => { setSelectedLead(lead); setInfoOpen(true); };
  const handleCloseInfo = () => { setInfoOpen(false); setSelectedLead(null); };
  const handleOpenDelete = (lead) => { setDeleteTarget(lead); setDeleteOpen(true); };
  const handleCloseDelete = () => { if (!deleting) { setDeleteOpen(false); setDeleteTarget(null); } };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const followupId = deleteTarget.followupId;
      const res = await fetch(`${URL}/followup-masters/${followupId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isActive: 0 }),
      });
      if (!res.ok) throw new Error(`Server error: ${res.status}`);
      setRows((prev) => prev.filter((r) => r.followupId !== followupId));
      const custName = deleteTarget?.enquiry?.customer?.customerName || deleteTarget?.customer?.customerName || 'Record';
      toast.success(`"${custName}" deleted successfully.`);
      setDeleteOpen(false);
      setDeleteTarget(null);
      setPage((p) => {
        const newTotal = Math.max(1, Math.ceil((filtered.length - 1) / PAGE_SIZE));
        return p > newTotal ? newTotal : p;
      });
    } catch (e) {
      setError('Delete failed: ' + e.message);
      setDeleteOpen(false);
    } finally {
      setDeleting(false);
    }
  };

  const handleSave = (lead, entry) => {
    const oldFollowupId = entry.oldFollowupId || lead.followupId;
    const newRow = {
      ...lead,
      followupId: entry.id,
      _isLocalNew: true,
      _parentFollowupId: oldFollowupId,
      fkLeadStatusId: Number(entry.status) || 0,
      callLeadDate: entry.leadDate ? new Date(entry.leadDate).toISOString() : (entry.savedAt || new Date().toISOString()),
      nextFollowupDate: entry.followDate ? `${entry.followDate}T00:00:00` : null,
      followupRemark: entry.note,
      fkcommunicationmediaId: Number(entry.mediaId) || lead.fkcommunicationmediaId,
      assignTo: Number(entry.assignedTo) || lead.assignTo,
      media: entry.media,
      leadStatus: entry.status,
      priority: Number(entry.priority) || 2,
      isActive: 1,
    };
    setRows((prev) => [newRow, ...prev.filter((r) => r.followupId !== oldFollowupId)]);
    setPage(1);
  };

  const selectSx = {
    borderRadius: '10px', bgcolor: '#ffffff',
    fontSize: '0.93rem', fontFamily: FONT, fontWeight: 500, color: LABEL,
    '& fieldset': { borderColor: '#cbd5e1', borderWidth: '1.5px' },
    '&:hover fieldset': { borderColor: '#6366f1' },
    '&.Mui-focused fieldset': { borderColor: NAVY, borderWidth: '2px' },
  };

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ width: '100%', minHeight: '100vh', display: 'flex', flexDirection: 'column', gap: { xs: 1.5, sm: 2 }, p: { xs: '10px 12px', sm: '10px 25px 10px 15px' }, fontFamily: FONT, boxSizing: 'border-box' }}>

      {/* ── Page Header ──────────────────────────────────────────────────────── */}
      <Box sx={{ mb: { xs: 0.5, sm: 1 }, pb: { xs: 1, sm: 1.5 }, borderBottom: `3px solid ${ORANGE}`, position: 'relative' }}>
        {/* Mobile layout: stacked */}
        {isMobile ? (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: '1.35rem', color: NAVY, lineHeight: 1.2 }}>
                Follow-Up
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip label={`${filtered.length}`} sx={{ bgcolor: NAVY, color: '#fff', fontFamily: FONT, fontWeight: 800, fontSize: '0.82rem', height: 28 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, px: 1.2, py: 0.5, bgcolor: `${NAVY}0d`, borderRadius: '50px', border: `1.5px solid ${NAVY}20` }}>
                  <Avatar sx={{ width: 22, height: 22, fontSize: '0.55rem', bgcolor: NAVY, fontFamily: FONT, fontWeight: 800 }}>{getInitials(loggedName || '')}</Avatar>
                  <Typography sx={{ fontFamily: FONT, fontSize: '0.75rem', fontWeight: 700, color: LABEL }}>{loggedName || '—'}</Typography>
                </Box>
              </Box>
            </Box>
            <Typography sx={{ fontFamily: FONT, fontSize: '0.8rem', color: '#6b7280', fontWeight: 500 }}>
              Track and manage all your leads
            </Typography>
          </Box>
        ) : (
          /* Desktop layout: centered with absolute right */
          <Box sx={{ textAlign: 'center' }}>
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
              <Box sx={{ width: 6, height: 32, bgcolor: ORANGE, borderRadius: '3px' }} />
              <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: '2rem', color: NAVY, lineHeight: 1.15, letterSpacing: '-0.5px' }}>
                Follow-Up Management
              </Typography>
              <Box sx={{ width: 6, height: 32, bgcolor: ORANGE, borderRadius: '3px' }} />
            </Box>
            <Typography sx={{ fontFamily: FONT, fontSize: '0.95rem', color: LABEL, fontWeight: 500 }}>
              Track and manage all your leads efficiently
            </Typography>
            <Box sx={{ position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)', display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.6, bgcolor: `${NAVY}0d`, borderRadius: '50px', border: `1.5px solid ${NAVY}20` }}>
                <Avatar sx={{ width: 26, height: 26, fontSize: '0.6rem', bgcolor: NAVY, fontFamily: FONT, fontWeight: 800 }}>{getInitials(loggedName || '')}</Avatar>
                <Box>
                  <Typography sx={{ fontFamily: FONT, fontSize: '0.62rem', fontWeight: 800, color: NAVY, letterSpacing: '0.5px', textTransform: 'uppercase', lineHeight: 1 }}>
                    {loggedRole === 1 ? '👑 Admin' : '👤 Employee'}
                  </Typography>
                  <Typography sx={{ fontFamily: FONT, fontSize: '0.78rem', fontWeight: 700, color: LABEL, lineHeight: 1.2 }}>{loggedName || '—'}</Typography>
                </Box>
              </Box>
              <Chip label={`${filtered.length} Record${filtered.length !== 1 ? 's' : ''}`} sx={{ bgcolor: NAVY, color: '#fff', fontFamily: FONT, fontWeight: 800, fontSize: '0.88rem', height: 34, px: 1 }} />
            </Box>
          </Box>
        )}
      </Box>

      {/* Employee banner */}
      {!loading && loggedRole !== 1 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: { xs: 1.5, sm: 2 }, py: 1.2, bgcolor: '#EFF6FF', border: `1.5px solid #1D4ED8`, borderRadius: '10px' }}>
          <Avatar sx={{ width: 28, height: 28, fontSize: '0.65rem', bgcolor: NAVY, fontFamily: FONT }}>{getInitials(loggedName || '')}</Avatar>
          <Typography sx={{ fontFamily: FONT, fontSize: { xs: '0.82rem', sm: '0.88rem' }, fontWeight: 600, color: NAVY, flex: 1 }}>
            Showing follow-ups assigned to <strong>{loggedName}</strong> only
          </Typography>
          <Chip label={`${filtered.length}`} sx={{ bgcolor: NAVY, color: '#fff', fontFamily: FONT, fontWeight: 800, fontSize: '0.75rem', height: 24 }} />
        </Box>
      )}

      {/* ── Filter Bar ─────────────────────────────────────────────────────── */}
      <Paper elevation={0} sx={{ px: { xs: 1.5, sm: 2.5 }, py: { xs: 1.5, sm: 1.8 }, borderRadius: '14px', border: '1.5px solid #e2e8f0', bgcolor: '#fff', boxShadow: '0 2px 12px rgba(15,31,92,0.06)', position: 'relative', zIndex: 10 }}>

        {/* Search row – always visible */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TextField size="small" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name / mobile..."
            InputProps={{
              startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 16, color: LABEL }} /></InputAdornment>,
              endAdornment: search ? <InputAdornment position="end"><IconButton size="small" onClick={() => setSearch('')}><Clear sx={{ fontSize: 13, color: LABEL }} /></IconButton></InputAdornment> : null,
            }}
            sx={{ ...fieldSx, flex: 1, '& .MuiOutlinedInput-root': { ...fieldSx['& .MuiOutlinedInput-root'], height: 40 } }} />

          {/* Mobile: toggle more filters button */}
          {isMobile && (
            <IconButton onClick={() => setFiltersOpen(v => !v)} size="small"
              sx={{ border: `1.5px solid ${filtersOpen ? NAVY : '#e2e8f0'}`, borderRadius: '10px', color: filtersOpen ? NAVY : LABEL, bgcolor: filtersOpen ? `${NAVY}10` : '#fff', width: 40, height: 40, flexShrink: 0, position: 'relative' }}>
              <FilterList sx={{ fontSize: 18 }} />
              {hasAnyFilter && !search && (
                <Box sx={{ position: 'absolute', top: 5, right: 5, width: 8, height: 8, borderRadius: '50%', bgcolor: ORANGE }} />
              )}
            </IconButton>
          )}

          {/* Desktop: refresh */}
          {!isMobile && (
            <Tooltip title="Refresh & Reset" arrow>
              <IconButton onClick={handleRefresh} size="small"
                sx={{ ml: 'auto', flexShrink: 0, border: `1.5px solid ${NAVY}20`, borderRadius: '8px', color: NAVY, bgcolor: `${NAVY}08`, width: 36, height: 36, '&:hover': { bgcolor: `${NAVY}14` } }}>
                <Refresh sx={{ fontSize: 17 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {/* Desktop: remaining filters inline */}
        {!isMobile && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mt: 1.5 }}>
            <TextField type="date" size="small" value={fromDate} onChange={(e) => setFromDate(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><CalendarToday sx={{ fontSize: 14, color: LABEL }} /></InputAdornment> }}
              sx={{ ...fieldSx, minWidth: 155, '& .MuiOutlinedInput-root': { ...fieldSx['& .MuiOutlinedInput-root'], height: 40 } }} />
            <TextField type="date" size="small" value={toDate} onChange={(e) => setToDate(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><CalendarToday sx={{ fontSize: 14, color: LABEL }} /></InputAdornment> }}
              sx={{ ...fieldSx, minWidth: 155, '& .MuiOutlinedInput-root': { ...fieldSx['& .MuiOutlinedInput-root'], height: 40 } }} />
            <Box sx={{ width: '1px', height: 28, bgcolor: '#e2e8f0', flexShrink: 0 }} />
            <Box sx={{ minWidth: 190 }}>
              <SearchableDropdown value={filterComm} onChange={setFilterComm} options={commMedia} placeholder="All Communication" icon={<CellTower />} getOptionId={(o) => o.communicationmediaId || o.id} getOptionLabel={(o) => o.communicationmediaName || o.communicationMedia || o.name || '—'} />
            </Box>
            <Box sx={{ minWidth: 170 }}>
              <SearchableDropdown value={filterArea} onChange={setFilterArea} options={areas} placeholder="All Areas" icon={<LocationOn />} getOptionId={(o) => o.areaId || o.id} getOptionLabel={(o) => o.areaName || o.area || o.name || '—'} />
            </Box>
            <Box sx={{ minWidth: 145 }}>
              <FormControl fullWidth size="small">
                <Select value={filterPriority} onChange={(e) => setFilterPriority(e.target.value)} displayEmpty sx={{ ...selectSx, height: 40 }}
                  renderValue={(val) => {
                    if (val === '') return <Typography sx={{ fontFamily: FONT, fontSize: '0.88rem', color: LABEL, fontWeight: 400 }}>All Priority</Typography>;
                    const opt = getPriorityOption(val);
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {React.cloneElement(opt.icon, { style: { color: opt.color, fontSize: 15 } })}
                        <Typography sx={{ fontFamily: FONT, fontSize: '0.88rem', fontWeight: 700, color: opt.color }}>{opt.label}</Typography>
                      </Box>
                    );
                  }}>
                  <MenuItem value="" sx={{ fontFamily: FONT }}>All Priority</MenuItem>
                  {PRIORITY_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value} sx={{ fontFamily: FONT, py: 1.2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        {React.cloneElement(o.icon, { style: { color: o.color, fontSize: 16 } })}
                        <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: '0.88rem', color: o.color }}>{o.label}</Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>
        )}

        {/* Mobile: collapsible filter panel */}
        {isMobile && (
          <Collapse in={filtersOpen}>
            <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 1.2 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                <Box>
                  <Typography sx={{ fontFamily: FONT, fontSize: '0.65rem', fontWeight: 800, color: '#9ca3af', letterSpacing: '0.8px', textTransform: 'uppercase', mb: 0.5 }}>From Date</Typography>
                  <TextField type="date" size="small" fullWidth value={fromDate} onChange={(e) => setFromDate(e.target.value)}
                    sx={{ ...fieldSx, '& .MuiOutlinedInput-root': { ...fieldSx['& .MuiOutlinedInput-root'], height: 38 } }} />
                </Box>
                <Box>
                  <Typography sx={{ fontFamily: FONT, fontSize: '0.65rem', fontWeight: 800, color: '#9ca3af', letterSpacing: '0.8px', textTransform: 'uppercase', mb: 0.5 }}>To Date</Typography>
                  <TextField type="date" size="small" fullWidth value={toDate} onChange={(e) => setToDate(e.target.value)}
                    sx={{ ...fieldSx, '& .MuiOutlinedInput-root': { ...fieldSx['& .MuiOutlinedInput-root'], height: 38 } }} />
                </Box>
              </Box>
              <Box>
                <Typography sx={{ fontFamily: FONT, fontSize: '0.65rem', fontWeight: 800, color: '#9ca3af', letterSpacing: '0.8px', textTransform: 'uppercase', mb: 0.5 }}>Communication</Typography>
                <SearchableDropdown value={filterComm} onChange={setFilterComm} options={commMedia} placeholder="All Communication" icon={<CellTower />} getOptionId={(o) => o.communicationmediaId || o.id} getOptionLabel={(o) => o.communicationmediaName || o.communicationMedia || o.name || '—'} />
              </Box>
              <Box>
                <Typography sx={{ fontFamily: FONT, fontSize: '0.65rem', fontWeight: 800, color: '#9ca3af', letterSpacing: '0.8px', textTransform: 'uppercase', mb: 0.5 }}>Area</Typography>
                <SearchableDropdown value={filterArea} onChange={setFilterArea} options={areas} placeholder="All Areas" icon={<LocationOn />} getOptionId={(o) => o.areaId || o.id} getOptionLabel={(o) => o.areaName || o.area || o.name || '—'} />
              </Box>
              <Box>
                <Typography sx={{ fontFamily: FONT, fontSize: '0.65rem', fontWeight: 800, color: '#9ca3af', letterSpacing: '0.8px', textTransform: 'uppercase', mb: 0.5 }}>Priority</Typography>
                <FormControl fullWidth size="small">
                  <Select value={filterPriority} onChange={(e) => setFilterPriority(e.target.value)} displayEmpty sx={{ ...selectSx, height: 40 }}
                    renderValue={(val) => {
                      if (val === '') return <Typography sx={{ fontFamily: FONT, fontSize: '0.88rem', color: LABEL, fontWeight: 400 }}>All Priority</Typography>;
                      const opt = getPriorityOption(val);
                      return (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {React.cloneElement(opt.icon, { style: { color: opt.color, fontSize: 15 } })}
                          <Typography sx={{ fontFamily: FONT, fontSize: '0.88rem', fontWeight: 700, color: opt.color }}>{opt.label}</Typography>
                        </Box>
                      );
                    }}>
                    <MenuItem value="" sx={{ fontFamily: FONT }}>All Priority</MenuItem>
                    {PRIORITY_OPTIONS.map((o) => (
                      <MenuItem key={o.value} value={o.value} sx={{ fontFamily: FONT, py: 1.2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          {React.cloneElement(o.icon, { style: { color: o.color, fontSize: 16 } })}
                          <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: '0.88rem', color: o.color }}>{o.label}</Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button fullWidth variant="outlined" onClick={handleRefresh} startIcon={<Refresh sx={{ fontSize: 15 }} />}
                  sx={{ borderRadius: '10px', textTransform: 'none', fontFamily: FONT, fontWeight: 700, fontSize: '0.85rem', py: 1, color: LABEL, borderColor: '#cbd5e1' }}>
                  Reset
                </Button>
                <Button fullWidth variant="contained" onClick={() => setFiltersOpen(false)}
                  sx={{ borderRadius: '10px', textTransform: 'none', fontFamily: FONT, fontWeight: 700, fontSize: '0.85rem', py: 1, background: `linear-gradient(135deg, ${NAVY}, #1d4ed8)` }}>
                  Apply
                </Button>
              </Box>
            </Box>
          </Collapse>
        )}

        {hasAnyFilter && (
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 1.2 }}>
            <Chip label={`${filtered.length} result${filtered.length !== 1 ? 's' : ''}`} onDelete={handleRefresh} size="small"
              sx={{ bgcolor: '#fef3c7', color: '#92400e', fontFamily: FONT, fontWeight: 700, fontSize: '0.78rem', height: 30 }} />
          </Box>
        )}
      </Paper>

      <TableContainer component={Paper} elevation={0} sx={{ borderRadius: '16px', border: '1.5px solid #e2e8f0', overflow: 'auto', boxShadow: '0 2px 12px rgba(15,31,92,0.06)' }}>
        <Table stickyHeader sx={{ minWidth: isTablet ? 700 : 'auto' }}>
          <TableHead>
            <TableRow>
              {['#', 'Customer', 'Mobile', 'Area', 'Communication', 'Priority', 'Last Contact', 'Assigned To', 'Status', 'Actions'].map((h) => (
                <TableCell key={h} sx={{ bgcolor: '#f8fafc', fontFamily: FONT, fontWeight: 800, color: LABEL, fontSize: '0.73rem', letterSpacing: '0.9px', textTransform: 'uppercase', whiteSpace: 'nowrap', py: 2.2, borderBottom: '2px solid #e5e7eb' }}>
                  {h}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={10} sx={{ textAlign: 'center', py: 10 }}>
                  <CircularProgress size={38} sx={{ color: NAVY }} />
                  <Typography sx={{ fontFamily: FONT, fontSize: '1rem', color: LABEL, mt: 2, fontWeight: 500 }}>Loading...</Typography>
                </TableCell>
              </TableRow>
            ) : filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} sx={{ textAlign: 'center', py: 10 }}>
                  <Typography sx={{ fontFamily: FONT, fontSize: '1rem', color: LABEL, fontWeight: 500 }}>No records found</Typography>
                </TableCell>
              </TableRow>
            ) : paginated.map((row, idx) => {
              const cust = row.enquiry?.customer || row.customer || {};
              const areaNames = getAreaNames(row);
              const emp = employees.find((e) => e.employeeId == row.assignTo) || null;
              const isNew = row._isLocalNew;
              const rowNum = (page - 1) * PAGE_SIZE + idx + 1;
              const priorityOpt = getPriorityOption(normP(row.priority));

              return (
                <TableRow key={String(row.followupId)} sx={{ bgcolor: isNew ? '#f0fdf4' : '#fff', '&:hover': { bgcolor: isNew ? '#dcfce7' : '#f8faff' }, transition: 'background 0.12s' }}>
                  <TableCell sx={{ fontFamily: FONT, fontSize: '0.88rem', color: LABEL, fontWeight: 600, py: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {rowNum}
                      {isNew && <Chip label="New" size="small" sx={{ height: 16, fontSize: '0.58rem', fontWeight: 800, bgcolor: '#dcfce7', color: '#166534', fontFamily: FONT, px: 0.3, ml: 0.5 }} />}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ py: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar sx={{ bgcolor: getAvatarColor(cust.customerName || ''), width: 38, height: 38, fontSize: '0.85rem', fontFamily: FONT, fontWeight: 800 }}>{getInitials(cust.customerName)}</Avatar>
                      <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: '0.93rem', color: LABEL }}>{cust.customerName || '—'}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontFamily: FONT, fontSize: '0.9rem', color: LABEL, fontWeight: 500, py: 2 }}>{cust.mobileno || '—'}</TableCell>
                  <TableCell sx={{ py: 2 }}>
                    {areaNames !== '—' ? (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {(row.enquiry?.areas || []).map((a) => (
                          <Chip key={a.areaId} size="small" label={a.areaName}
                            icon={<LocationOn sx={{ fontSize: '11px !important', color: `${NAVY} !important`, ml: '6px !important' }} />}
                            sx={{ bgcolor: '#f0f4ff', color: NAVY, fontWeight: 700, fontSize: '0.75rem', fontFamily: FONT, border: `1px solid #c7d2fe`, height: 24 }} />
                        ))}
                        {(!row.enquiry?.areas || row.enquiry.areas.length === 0) && (
                          <Typography sx={{ fontFamily: FONT, fontSize: '0.9rem', color: LABEL, fontWeight: 500 }}>{areaNames}</Typography>
                        )}
                      </Box>
                    ) : <Typography sx={{ fontFamily: FONT, fontSize: '0.88rem', color: LABEL }}>—</Typography>}
                  </TableCell>
                  <TableCell sx={{ py: 2 }}><MediaPill media={row.media} mediaId={row.fkcommunicationmediaId} commMedia={commMedia} /></TableCell>
                  <TableCell sx={{ py: 2 }}>
                    <Chip size="small" label={priorityOpt.label}
                      icon={React.cloneElement(priorityOpt.icon, { style: { color: priorityOpt.color, marginLeft: 6, fontSize: 14 } })}
                      sx={{ bgcolor: priorityOpt.bg, color: priorityOpt.color, fontWeight: 700, fontSize: '0.78rem', fontFamily: FONT, border: `1.5px solid ${priorityOpt.border}`, height: 28, px: 0.3 }} />
                  </TableCell>
                  <TableCell sx={{ py: 2 }}>
                    <Typography sx={{ fontFamily: FONT, fontSize: '0.9rem', fontWeight: 600, color: LABEL }}>{fmtDate(row.callLeadDate)}</Typography>
                    {row.nextFollowupDate && <Typography sx={{ fontFamily: FONT, fontSize: '0.74rem', color: LABEL, fontWeight: 500, mt: 0.2 }}>Next: {fmtDate(row.nextFollowupDate)}</Typography>}
                  </TableCell>
                  <TableCell sx={{ py: 2 }}>
                    {emp ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 30, height: 30, fontSize: '0.66rem', bgcolor: getAvatarColor(emp.employeeName), fontFamily: FONT }}>{getInitials(emp.employeeName)}</Avatar>
                        <Box>
                          <Typography sx={{ fontFamily: FONT, fontSize: '0.88rem', fontWeight: 700, color: LABEL }}>{emp.employeeName}</Typography>
                          {emp.designation && <Typography sx={{ fontFamily: FONT, fontSize: '0.72rem', color: LABEL }}>{emp.designation}</Typography>}
                        </Box>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PersonOutlined sx={{ fontSize: 18, color: '#cbd5e1' }} />
                        <Typography sx={{ fontFamily: FONT, fontSize: '0.88rem', color: LABEL, fontWeight: 500 }}>Unassigned</Typography>
                      </Box>
                    )}
                  </TableCell>
                  <TableCell sx={{ py: 2 }}>
                    <StatusChip status={row.fkLeadStatusId ? String(row.fkLeadStatusId) : row.leadStatus} statusMap={statusMap} />
                  </TableCell>
                  <TableCell sx={{ py: 2 }}>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <Tooltip title="View Details & History" arrow>
                        <IconButton onClick={() => handleOpenInfo(row)} size="small"
                          sx={{ border: `1.5px solid ${NAVY}30`, borderRadius: '9px', color: NAVY, bgcolor: `${NAVY}08`, width: 34, height: 34, '&:hover': { bgcolor: `${NAVY}18` } }}>
                          <InfoOutlined sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Follow-Up" arrow>
                        <IconButton onClick={() => handleOpenDelete(row)} size="small"
                          sx={{ border: '1.5px solid #fecaca', borderRadius: '9px', color: '#dc2626', bgcolor: '#fff5f5', width: 34, height: 34, '&:hover': { bgcolor: '#fee2e2', borderColor: '#dc2626' }, transition: 'all 0.15s' }}>
                          <DeleteIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                      <Button variant="contained" size="small" onClick={() => handleOpenFollowUp(row)}
                        startIcon={<ArrowForward sx={{ fontSize: 13 }} />}
                        sx={{ fontFamily: FONT, fontSize: '0.82rem', textTransform: 'none', fontWeight: 700, borderRadius: '9px', px: 2, py: 0.9, whiteSpace: 'nowrap', background: `linear-gradient(135deg, ${NAVY}, #1d4ed8)`, boxShadow: `0 2px 8px rgba(15,31,92,0.28)`, '&:hover': { background: `linear-gradient(135deg, #0c1745, #1e3a8a)` } }}>
                        Follow-Up
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>


      {/* ── Pagination ───────────────────────────────────────────────────────── */}
      {!loading && filtered.length > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: { xs: 1, sm: 2 }, px: 1, flexWrap: 'wrap', gap: 1 }}>
          <Typography sx={{ fontFamily: FONT, fontSize: { xs: '0.78rem', sm: '0.83rem' }, color: LABEL, fontWeight: 500 }}>
            <strong>{(page - 1) * PAGE_SIZE + 1}</strong>–<strong>{Math.min(page * PAGE_SIZE, filtered.length)}</strong> of <strong>{filtered.length}</strong>
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
            <IconButton onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} size="small"
              sx={{ width: 34, height: 34, borderRadius: '9px', border: `1.5px solid ${page === 1 ? '#e2e8f0' : NAVY + '30'}`, color: page === 1 ? '#cbd5e1' : NAVY, bgcolor: page === 1 ? '#f8fafc' : `${NAVY}08` }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </IconButton>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
              .reduce((acc, p, i, arr) => { if (i > 0 && p - arr[i - 1] > 1) acc.push('...'); acc.push(p); return acc; }, [])
              .map((p, i) =>
                p === '...' ? (
                  <Typography key={`ellipsis-${i}`} sx={{ fontFamily: FONT, fontSize: '0.85rem', color: LABEL, px: 0.5 }}>…</Typography>
                ) : (
                  <IconButton key={p} onClick={() => setPage(p)} size="small"
                    sx={{ width: 34, height: 34, borderRadius: '9px', fontFamily: FONT, fontSize: '0.85rem', fontWeight: p === page ? 800 : 600, border: `1.5px solid ${p === page ? NAVY : '#e2e8f0'}`, color: p === page ? '#fff' : LABEL, bgcolor: p === page ? NAVY : '#fff', '&:hover': { bgcolor: p === page ? '#0c1745' : '#f0f4ff', borderColor: NAVY }, transition: 'all 0.15s' }}>
                    {p}
                  </IconButton>
                )
              )}
            <IconButton onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} size="small"
              sx={{ width: 34, height: 34, borderRadius: '9px', border: `1.5px solid ${page === totalPages ? '#e2e8f0' : NAVY + '30'}`, color: page === totalPages ? '#cbd5e1' : NAVY, bgcolor: page === totalPages ? '#f8fafc' : `${NAVY}08` }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </IconButton>
          </Box>
        </Box>
      )}

      {/* Modals */}
      {selectedLead && followUpOpen && (
        <FollowUpModal open={followUpOpen} onClose={handleCloseFollowUp} lead={selectedLead} onSave={handleSave}
          commMedia={commMedia} employees={employees} statusOptions={statusOptions} statusMap={statusMap} />
      )}
      {selectedLead && infoOpen && (
        <InfoModal open={infoOpen} onClose={handleCloseInfo} lead={selectedLead}
          commMedia={commMedia} employees={employees} statusMap={statusMap} />
      )}
      <DeleteConfirmDialog open={deleteOpen} onClose={handleCloseDelete} onConfirm={handleConfirmDelete} lead={deleteTarget} deleting={deleting} />

      <Toaster position="top-right"
        toastOptions={{
          success: { duration: 4000, style: { background: '#16a34a', color: '#ffffff', fontFamily: FONT, fontWeight: 700, fontSize: '0.92rem', borderRadius: '10px', padding: '12px 18px', boxShadow: '0 4px 20px rgba(22,163,74,0.35)' }, iconTheme: { primary: '#ffffff', secondary: '#16a34a' } },
          error: { duration: 5000, style: { background: '#dc2626', color: '#ffffff', fontFamily: FONT, fontWeight: 700, fontSize: '0.92rem', borderRadius: '10px', padding: '12px 18px', boxShadow: '0 4px 20px rgba(220,38,38,0.35)' }, iconTheme: { primary: '#ffffff', secondary: '#dc2626' } },
        }}
      />
      <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError('')}>
        <Alert severity="error" onClose={() => setError('')} sx={{ fontFamily: FONT, fontSize: '0.95rem' }}>{error}</Alert>
      </Snackbar>
    </Box>
  );
};

export default FollowUpManagement; 

